import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { recordReads } from '../../utils/readTelemetry';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  FiDownload, FiPrinter, FiSearch, FiChevronDown, FiChevronRight,
  FiFilter, FiRefreshCw, FiDollarSign, FiTrendingUp, FiAlertCircle,
  FiCheckCircle, FiEye, FiEyeOff, FiCalendar, FiLayers
} from 'react-icons/fi';
import { addPrintBrand } from '../../utils/printBranding';

// ─── Constants ───────────────────────────────────────────────────────────────

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#17A2B8',
  '#6F42C1'
];

const ACCOUNT_TYPES = {
  ASSET: 'asset',
  LIABILITY: 'liability',
  EQUITY: 'equity',
  REVENUE: 'revenue',
  EXPENSE: 'expense'
};

// ─── Helper Functions ────────────────────────────────────────────────────────

// ACC-01 fix (2026-09-30): Assets AND Expenses are debit-normal. Expenses were
// previously treated as credit-normal, so their balances came out negative and
// Retained Earnings (= Revenue − Expense) ADDED them instead of subtracting,
// leaving every Balance Sheet out by 2 × expenses.
const calculateBalance = (accountType, totalDebit, totalCredit) => {
  const t = String(accountType || '').toLowerCase();
  if (t === ACCOUNT_TYPES.ASSET || t === ACCOUNT_TYPES.EXPENSE) {
    return totalDebit - totalCredit;
  }
  // Liability, Equity, Revenue: credit-normal
  return totalCredit - totalDebit;
};

const buildAccountHierarchy = (accounts, balances, parentId = null, level = 1) => {
  const children = accounts
    .filter(a => {
      if (parentId === null) return (!a.parentId || a.parentId === '') && a.level === level;
      return a.parentId === parentId;
    })
    .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

  return children.map(account => {
    const childNodes = buildAccountHierarchy(accounts, balances, account.id, (account.level || level) + 1);
    const ownBalance = balances[account.id] || 0;
    const childrenBalance = childNodes.reduce((sum, c) => sum + c.totalBalance, 0);
    // ACC-15: count every bottom-level account (same rule as the Trial Balance),
    // incl. accounts added by hand that were saved without the postable flag.
    const isLeaf = account.isPostable || account.level === 5;
    const totalBalance = isLeaf ? ownBalance + childrenBalance : childrenBalance;

    return {
      ...account,
      ownBalance,
      totalBalance,
      children: childNodes,
      hasChildren: childNodes.length > 0
    };
  });
};

const flattenHierarchy = (nodes, depth = 0) => {
  const rows = [];
  nodes.forEach(node => {
    rows.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      rows.push(...flattenHierarchy(node.children, depth + 1));
    }
  });
  return rows;
};

const getLevel2Groups = (hierarchyNodes) => {
  const groups = [];
  const collectLevel2 = (nodes, depth) => {
    nodes.forEach(node => {
      if (depth === 1 || (node.level === 2)) {
        groups.push({ name: node.name, value: Math.abs(node.totalBalance) });
      } else if (node.children && node.children.length > 0) {
        collectLevel2(node.children, depth + 1);
      }
    });
  };
  collectLevel2(hierarchyNodes, 0);
  return groups.filter(g => g.value > 0);
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
};

const parseFirestoreDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal.toDate) return dateVal.toDate();
  if (dateVal.seconds) return new Date(dateVal.seconds * 1000);
  if (typeof dateVal === 'string') return new Date(dateVal);
  if (dateVal instanceof Date) return dateVal;
  return null;
};

// ─── Component ───────────────────────────────────────────────────────────────

const BalanceSheet = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();

  // State: Data
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [prevYearTransactions, setPrevYearTransactions] = useState([]);

  // State: Filters
  const [selectedFYId, setSelectedFYId] = useState('');
  const [asOfDate, setAsOfDate] = useState('');
  const [showDetails, setShowDetails] = useState(true);
  const [comparisonMode, setComparisonMode] = useState('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [showZeroBalances, setShowZeroBalances] = useState(false);

  // State: UI
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  // Whether the last Generate also fetched previous-year data (comparison mode)
  const [prevYearLoaded, setPrevYearLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showFilters, setShowFilters] = useState(true);

  const printRef = useRef(null);

  // ─── Fetch Financial Years ───────────────────────────────────────────────

  useEffect(() => {
    if (!activeCompany?.id) return;

    const fetchFinancialYears = async () => {
      try {
        const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
        const fyQuery = query(fyRef, orderBy('startDate', 'desc'));
        const snapshot = await getDocs(fyQuery);
        recordReads(activeCompany.id, snapshot.size);
        const fys = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFinancialYears(fys);
      } catch (err) {
        console.error('Error fetching financial years:', err);
      }
    };

    fetchFinancialYears();
  }, [activeCompany?.id]);

  // ─── Set default FY and As-Of Date ────────────────────────────────────────

  useEffect(() => {
    if (activeFinancialYear && !selectedFYId) {
      setSelectedFYId(activeFinancialYear.id);
    }
  }, [activeFinancialYear, selectedFYId]);

  useEffect(() => {
    if (selectedFYId && financialYears.length > 0) {
      const fy = financialYears.find(f => f.id === selectedFYId);
      if (fy) {
        const endDate = parseFirestoreDate(fy.endDate);
        const today = new Date();
        const defaultDate = endDate && endDate < today ? endDate : today;
        setAsOfDate(formatDateForInput(defaultDate));
      }
    }
  }, [selectedFYId, financialYears]);

  // ─── Fetch Accounts (called from Generate) ───────────────────────────────

  const fetchAccounts = useCallback(async () => {
    if (!activeCompany?.id) return [];
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const snapshot = await getDocs(accountsRef);
      recordReads(activeCompany.id, snapshot.size);
      const accts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccounts(accts);

      // Initialize expanded sections for top-level groups
      const expanded = {};
      accts
        .filter(a => !a.parentId || a.parentId === '')
        .forEach(a => { expanded[a.id] = true; });
      // Also expand type sections
      expanded['section-asset'] = true;
      expanded['section-liability'] = true;
      expanded['section-equity'] = true;
      setExpandedSections(expanded);
      return accts;
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again.');
      return [];
    }
  }, [activeCompany?.id]);

  // ─── Fetch Transactions ──────────────────────────────────────────────────

  // Fetches the WHOLE selected FY (no date cutoff) — the as-of date cutoff is
  // applied client-side below, so changing the As-of Date after Generate
  // recomputes from already-loaded data with zero extra Firestore reads
  // (same behavior as TrialBalance.js).
  const fetchTransactionsForFY = useCallback(async (fyId) => {
    if (!activeCompany?.id || !fyId) return [];

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const transQuery = query(
        transRef,
        where('financialYearId', '==', fyId),
        where('status', 'in', ['Posted', 'Draft'])
      );
      const snapshot = await getDocs(transQuery);
      recordReads(activeCompany.id, snapshot.size);
      const allTrans = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      return allTrans;
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return [];
    }
  }, [activeCompany?.id, activeBranch]);

  // ─── Generate Report ──────────────────────────────────────────────────────

  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId || !asOfDate) return;

    setLoading(true);
    setHasGenerated(true);
    setError(null);

    try {
      const [, trans] = await Promise.all([
        fetchAccounts(),
        fetchTransactionsForFY(selectedFYId)
      ]);
      setTransactions(trans);

      // If comparison mode, fetch previous year transactions (whole FY;
      // the equivalent as-of cutoff is applied client-side below)
      if (comparisonMode === 'previous') {
        const currentFY = financialYears.find(f => f.id === selectedFYId);
        const currentStart = currentFY ? parseFirestoreDate(currentFY.startDate) : null;
        // Find the FY that ends before current FY starts
        const prevFY = currentStart
          ? financialYears.find(f => {
              const fEnd = parseFirestoreDate(f.endDate);
              return fEnd && fEnd < currentStart;
            })
          : null;
        if (prevFY) {
          const prevTrans = await fetchTransactionsForFY(prevFY.id);
          setPrevYearTransactions(prevTrans);
        } else {
          setPrevYearTransactions([]);
        }
        setPrevYearLoaded(true);
      } else {
        setPrevYearTransactions([]);
        setPrevYearLoaded(false);
      }
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Client-side As-of Date Cutoff (zero reads on date change) ────────────

  // Byte-identical to the old fetch-time cutoff filter, now applied in-memory
  const filterByCutoffDate = useCallback((transList, cutoffDate) => {
    if (!cutoffDate) return [];
    const cutoff = new Date(cutoffDate);
    cutoff.setHours(23, 59, 59, 999);

    return transList.filter(t => {
      const docDate = parseFirestoreDate(t.docDate);
      return docDate && docDate <= cutoff;
    });
  }, []);

  const cutoffTransactions = useMemo(
    () => filterByCutoffDate(transactions, asOfDate),
    [filterByCutoffDate, transactions, asOfDate]
  );

  // Calculate equivalent as-of date in previous year (same math as before)
  const prevYearCutoffDate = useMemo(() => {
    if (!selectedFYId || !asOfDate) return '';
    const currentFY = financialYears.find(f => f.id === selectedFYId);
    if (!currentFY) return '';
    const currentStart = parseFirestoreDate(currentFY.startDate);
    if (!currentStart) return '';
    // Find the FY that ends before current FY starts
    const prevFY = financialYears.find(f => {
      const fEnd = parseFirestoreDate(f.endDate);
      return fEnd && fEnd < currentStart;
    });
    if (!prevFY) return '';
    // Calculate equivalent as-of date in previous year
    const currentAsOf = new Date(asOfDate);
    const currentFYStart = parseFirestoreDate(currentFY.startDate);
    const prevFYStart = parseFirestoreDate(prevFY.startDate);
    const daysDiff = Math.floor((currentAsOf - currentFYStart) / (1000 * 60 * 60 * 24));
    const prevAsOf = new Date(prevFYStart);
    prevAsOf.setDate(prevAsOf.getDate() + daysDiff);
    const prevEndDate = parseFirestoreDate(prevFY.endDate);
    const prevCutoff = prevAsOf <= prevEndDate ? prevAsOf : prevEndDate;
    return formatDateForInput(prevCutoff);
  }, [selectedFYId, asOfDate, financialYears]);

  const cutoffPrevYearTransactions = useMemo(
    () => filterByCutoffDate(prevYearTransactions, prevYearCutoffDate),
    [filterByCutoffDate, prevYearTransactions, prevYearCutoffDate]
  );

  // ─── Compute Balances ────────────────────────────────────────────────────

  const computeBalances = useCallback((transList) => {
    const balances = {};
    transList.forEach(t => {
      if (!t.accountId) return;
      if (!balances[t.accountId]) {
        balances[t.accountId] = { debit: 0, credit: 0 };
      }
      balances[t.accountId].debit += (t.debitAmount || 0);
      balances[t.accountId].credit += (t.creditAmount || 0);
    });

    const result = {};
    accounts.forEach(acct => {
      const b = balances[acct.id] || { debit: 0, credit: 0 };
      result[acct.id] = calculateBalance(acct.accountType, b.debit, b.credit);
    });

    return result;
  }, [accounts]);

  const currentBalances = useMemo(() => computeBalances(cutoffTransactions), [computeBalances, cutoffTransactions]);
  const previousBalances = useMemo(() => computeBalances(cutoffPrevYearTransactions), [computeBalances, cutoffPrevYearTransactions]);

  // ─── Retained Earnings Calculation ────────────────────────────────────────

  const computeRetainedEarnings = useCallback((balances) => {
    let totalRevenue = 0;
    let totalExpense = 0;
    accounts.forEach(acct => {
      if (acct.accountType === ACCOUNT_TYPES.REVENUE) {
        totalRevenue += (balances[acct.id] || 0);
      }
      if (acct.accountType === ACCOUNT_TYPES.EXPENSE) {
        totalExpense += (balances[acct.id] || 0);
      }
    });
    return totalRevenue - totalExpense;
  }, [accounts]);

  const currentRetainedEarnings = useMemo(
    () => computeRetainedEarnings(currentBalances),
    [computeRetainedEarnings, currentBalances]
  );

  const previousRetainedEarnings = useMemo(
    () => computeRetainedEarnings(previousBalances),
    [computeRetainedEarnings, previousBalances]
  );

  // ─── Build Hierarchies ───────────────────────────────────────────────────

  const buildHierarchyForType = useCallback((accountType, balances) => {
    const typeAccounts = accounts.filter(a => a.accountType === accountType);
    return buildAccountHierarchy(typeAccounts, balances, null, 1);
  }, [accounts]);

  const assetHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.ASSET, currentBalances), [buildHierarchyForType, currentBalances]);
  const liabilityHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.LIABILITY, currentBalances), [buildHierarchyForType, currentBalances]);
  const equityHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.EQUITY, currentBalances), [buildHierarchyForType, currentBalances]);

  const prevAssetHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.ASSET, previousBalances), [buildHierarchyForType, previousBalances]);
  const prevLiabilityHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.LIABILITY, previousBalances), [buildHierarchyForType, previousBalances]);
  const prevEquityHierarchy = useMemo(() => buildHierarchyForType(ACCOUNT_TYPES.EQUITY, previousBalances), [buildHierarchyForType, previousBalances]);

  // ─── Totals ──────────────────────────────────────────────────────────────

  const totalAssets = useMemo(() => assetHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [assetHierarchy]);
  const totalLiabilities = useMemo(() => liabilityHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [liabilityHierarchy]);
  const totalEquity = useMemo(() => equityHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [equityHierarchy]);
  const totalEquityWithRE = useMemo(() => totalEquity + currentRetainedEarnings, [totalEquity, currentRetainedEarnings]);
  const balanceDifference = useMemo(() => totalAssets - (totalLiabilities + totalEquityWithRE), [totalAssets, totalLiabilities, totalEquityWithRE]);

  const prevTotalAssets = useMemo(() => prevAssetHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [prevAssetHierarchy]);
  const prevTotalLiabilities = useMemo(() => prevLiabilityHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [prevLiabilityHierarchy]);
  const prevTotalEquity = useMemo(() => prevEquityHierarchy.reduce((sum, n) => sum + n.totalBalance, 0), [prevEquityHierarchy]);
  const prevTotalEquityWithRE = useMemo(() => prevTotalEquity + previousRetainedEarnings, [prevTotalEquity, previousRetainedEarnings]);

  // ─── Search Filter ────────────────────────────────────────────────────────

  const filterNodesBySearch = useCallback((nodes, term) => {
    if (!term) return nodes;
    const lower = term.toLowerCase();
    const filterRecursive = (nodeList) => {
      return nodeList.reduce((acc, node) => {
        const nameMatch = (node.name || '').toLowerCase().includes(lower);
        const codeMatch = (node.code || '').toLowerCase().includes(lower);
        const filteredChildren = filterRecursive(node.children || []);

        if (nameMatch || codeMatch || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children: filteredChildren.length > 0 ? filteredChildren : node.children
          });
        }
        return acc;
      }, []);
    };
    return filterRecursive(nodes);
  }, []);

  const filterZeroBalances = useCallback((nodes) => {
    if (showZeroBalances) return nodes;
    const filterRecursive = (nodeList) => {
      return nodeList.reduce((acc, node) => {
        const filteredChildren = filterRecursive(node.children || []);
        if (Math.abs(node.totalBalance) > 0.005 || filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        }
        return acc;
      }, []);
    };
    return filterRecursive(nodes);
  }, [showZeroBalances]);

  const displayAssets = useMemo(() => filterZeroBalances(filterNodesBySearch(assetHierarchy, searchTerm)), [assetHierarchy, searchTerm, filterNodesBySearch, filterZeroBalances]);
  const displayLiabilities = useMemo(() => filterZeroBalances(filterNodesBySearch(liabilityHierarchy, searchTerm)), [liabilityHierarchy, searchTerm, filterNodesBySearch, filterZeroBalances]);
  const displayEquity = useMemo(() => filterZeroBalances(filterNodesBySearch(equityHierarchy, searchTerm)), [equityHierarchy, searchTerm, filterNodesBySearch, filterZeroBalances]);

  // ─── Chart Data ──────────────────────────────────────────────────────────

  const barChartData = useMemo(() => {
    const data = [
      { name: 'Assets', current: Math.abs(totalAssets), type: 'Assets' },
      { name: 'Liabilities', current: Math.abs(totalLiabilities), type: 'Liabilities' },
      { name: 'Equity', current: Math.abs(totalEquityWithRE), type: 'Equity' }
    ];
    if (comparisonMode === 'previous') {
      data[0].previous = Math.abs(prevTotalAssets);
      data[1].previous = Math.abs(prevTotalLiabilities);
      data[2].previous = Math.abs(prevTotalEquityWithRE);
    }
    return data;
  }, [totalAssets, totalLiabilities, totalEquityWithRE, prevTotalAssets, prevTotalLiabilities, prevTotalEquityWithRE, comparisonMode]);

  const assetPieData = useMemo(() => getLevel2Groups(assetHierarchy), [assetHierarchy]);
  const liabEquityPieData = useMemo(() => {
    const liabGroups = getLevel2Groups(liabilityHierarchy);
    const eqGroups = getLevel2Groups(equityHierarchy);
    if (Math.abs(currentRetainedEarnings) > 0.005) {
      eqGroups.push({ name: 'Retained Earnings', value: Math.abs(currentRetainedEarnings) });
    }
    return [...liabGroups, ...eqGroups];
  }, [liabilityHierarchy, equityHierarchy, currentRetainedEarnings]);

  // ─── Toggle Section ──────────────────────────────────────────────────────

  const toggleSection = useCallback((id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const expandAll = useCallback(() => {
    const expanded = {};
    accounts.forEach(a => { expanded[a.id] = true; });
    expanded['section-asset'] = true;
    expanded['section-liability'] = true;
    expanded['section-equity'] = true;
    setExpandedSections(expanded);
  }, [accounts]);

  const collapseAll = useCallback(() => {
    setExpandedSections({
      'section-asset': true,
      'section-liability': true,
      'section-equity': true
    });
  }, []);

  // ─── CSV Export ──────────────────────────────────────────────────────────

  const exportCSV = useCallback(() => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const fyLabel = selectedFY ? (selectedFY.name || selectedFY.label || selectedFYId) : selectedFYId;

    const lines = [];
    lines.push(`Balance Sheet - ${activeCompany?.companyName || 'Company'}`);
    lines.push(`As of: ${asOfDate}`);
    lines.push(`Financial Year: ${fyLabel}`);
    lines.push('');

    if (comparisonMode === 'previous') {
      lines.push('Account Code,Account Name,Current Period,Previous Period,Change,Change %');
    } else {
      lines.push('Account Code,Account Name,Balance');
    }

    const addNodes = (nodes, prevHierarchy, indent = '') => {
      nodes.forEach(node => {
        const bal = node.totalBalance;
        const prevNode = findNodeInHierarchy(prevHierarchy, node.id);
        const prevBal = prevNode ? prevNode.totalBalance : 0;
        const change = bal - prevBal;
        const changePct = prevBal !== 0 ? ((change / Math.abs(prevBal)) * 100).toFixed(1) + '%' : '-';

        if (comparisonMode === 'previous') {
          lines.push(`${node.code || ''},${indent}${(node.name || '').replace(/,/g, ' ')},${bal.toFixed(2)},${prevBal.toFixed(2)},${change.toFixed(2)},${changePct}`);
        } else {
          lines.push(`${node.code || ''},${indent}${(node.name || '').replace(/,/g, ' ')},${bal.toFixed(2)}`);
        }

        if (node.children && node.children.length > 0) {
          addNodes(node.children, prevHierarchy ? (prevNode?.children || []) : [], indent + '  ');
        }
      });
    };

    lines.push('');
    lines.push('ASSETS');
    addNodes(displayAssets, prevAssetHierarchy);
    if (comparisonMode === 'previous') {
      lines.push(`,TOTAL ASSETS,${totalAssets.toFixed(2)},${prevTotalAssets.toFixed(2)},${(totalAssets - prevTotalAssets).toFixed(2)},`);
    } else {
      lines.push(`,TOTAL ASSETS,${totalAssets.toFixed(2)}`);
    }

    lines.push('');
    lines.push('LIABILITIES');
    addNodes(displayLiabilities, prevLiabilityHierarchy);
    if (comparisonMode === 'previous') {
      lines.push(`,TOTAL LIABILITIES,${totalLiabilities.toFixed(2)},${prevTotalLiabilities.toFixed(2)},${(totalLiabilities - prevTotalLiabilities).toFixed(2)},`);
    } else {
      lines.push(`,TOTAL LIABILITIES,${totalLiabilities.toFixed(2)}`);
    }

    lines.push('');
    lines.push('EQUITY');
    addNodes(displayEquity, prevEquityHierarchy);
    if (comparisonMode === 'previous') {
      lines.push(`,Retained Earnings,${currentRetainedEarnings.toFixed(2)},${previousRetainedEarnings.toFixed(2)},${(currentRetainedEarnings - previousRetainedEarnings).toFixed(2)},`);
      lines.push(`,TOTAL EQUITY,${totalEquityWithRE.toFixed(2)},${prevTotalEquityWithRE.toFixed(2)},${(totalEquityWithRE - prevTotalEquityWithRE).toFixed(2)},`);
    } else {
      lines.push(`,Retained Earnings,${currentRetainedEarnings.toFixed(2)}`);
      lines.push(`,TOTAL EQUITY,${totalEquityWithRE.toFixed(2)}`);
    }

    lines.push('');
    if (comparisonMode === 'previous') {
      const totalLE = totalLiabilities + totalEquityWithRE;
      const prevTotalLE = prevTotalLiabilities + prevTotalEquityWithRE;
      lines.push(`,TOTAL LIABILITIES & EQUITY,${totalLE.toFixed(2)},${prevTotalLE.toFixed(2)},${(totalLE - prevTotalLE).toFixed(2)},`);
    } else {
      lines.push(`,TOTAL LIABILITIES & EQUITY,${(totalLiabilities + totalEquityWithRE).toFixed(2)}`);
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Balance_Sheet_${asOfDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [
    activeCompany, asOfDate, selectedFYId, financialYears, comparisonMode,
    displayAssets, displayLiabilities, displayEquity,
    prevAssetHierarchy, prevLiabilityHierarchy, prevEquityHierarchy,
    totalAssets, totalLiabilities, totalEquityWithRE, currentRetainedEarnings,
    prevTotalAssets, prevTotalLiabilities, prevTotalEquityWithRE, previousRetainedEarnings
  ]);

  // ─── Find node helper ────────────────────────────────────────────────────

  const findNodeInHierarchy = (hierarchy, id) => {
    for (const node of hierarchy) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeInHierarchy(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // ─── Print ───────────────────────────────────────────────────────────────

  const handlePrint = useCallback(() => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const fyLabel = selectedFY ? (selectedFY.name || selectedFY.label || selectedFYId) : selectedFYId;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const buildPrintRows = (nodes, prevHierarchy, depth = 0) => {
      let html = '';
      nodes.forEach(node => {
        const indent = depth * 24;
        const isGroup = node.hasChildren;
        const fontWeight = isGroup ? 'font-weight: 600;' : 'font-weight: 400;';
        const fontSize = depth === 0 ? 'font-size: 14px;' : 'font-size: 13px;';
        const bg = depth === 0 ? 'background: #f8f0f2;' : '';

        const prevNode = findNodeInHierarchy(prevHierarchy || [], node.id);
        const prevBal = prevNode ? prevNode.totalBalance : 0;
        const change = node.totalBalance - prevBal;
        const changePct = prevBal !== 0 ? ((change / Math.abs(prevBal)) * 100).toFixed(1) : '0.0';

        html += `<tr style="${bg}">`;
        html += `<td style="padding: 6px 8px 6px ${8 + indent}px; ${fontWeight} ${fontSize} border-bottom: 1px solid #eee;">${node.code || ''}</td>`;
        html += `<td style="padding: 6px 8px; ${fontWeight} ${fontSize} border-bottom: 1px solid #eee;">${node.name || ''}</td>`;
        html += `<td style="padding: 6px 8px; text-align: right; ${fontWeight} ${fontSize} border-bottom: 1px solid #eee;">${formatCurrency(node.totalBalance)}</td>`;
        if (comparisonMode === 'previous') {
          html += `<td style="padding: 6px 8px; text-align: right; ${fontSize} border-bottom: 1px solid #eee;">${formatCurrency(prevBal)}</td>`;
          html += `<td style="padding: 6px 8px; text-align: right; ${fontSize} border-bottom: 1px solid #eee; color: ${change >= 0 ? '#28A745' : '#DC3545'};">${formatCurrency(change)}</td>`;
          html += `<td style="padding: 6px 8px; text-align: right; ${fontSize} border-bottom: 1px solid #eee;">${changePct}%</td>`;
        }
        html += '</tr>';

        if (showDetails && node.children && node.children.length > 0) {
          html += buildPrintRows(node.children, prevNode?.children || [], depth + 1);
        }
      });
      return html;
    };

    const colCount = comparisonMode === 'previous' ? 6 : 3;

    let tableContent = '';

    // Assets
    tableContent += `<tr><td colspan="${colCount}" style="padding: 12px 8px 6px; font-size: 16px; font-weight: 700; color: #800020; border-bottom: 2px solid #800020;">ASSETS</td></tr>`;
    tableContent += buildPrintRows(displayAssets, prevAssetHierarchy);
    tableContent += `<tr style="background: #800020; color: white;"><td colspan="2" style="padding: 8px; font-weight: 700;">TOTAL ASSETS</td><td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalAssets)}</td>`;
    if (comparisonMode === 'previous') {
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(prevTotalAssets)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalAssets - prevTotalAssets)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${prevTotalAssets !== 0 ? (((totalAssets - prevTotalAssets) / Math.abs(prevTotalAssets)) * 100).toFixed(1) : '0.0'}%</td>`;
    }
    tableContent += '</tr>';

    // Spacer
    tableContent += `<tr><td colspan="${colCount}" style="padding: 16px;"></td></tr>`;

    // Liabilities
    tableContent += `<tr><td colspan="${colCount}" style="padding: 12px 8px 6px; font-size: 16px; font-weight: 700; color: #800020; border-bottom: 2px solid #800020;">LIABILITIES</td></tr>`;
    tableContent += buildPrintRows(displayLiabilities, prevLiabilityHierarchy);
    tableContent += `<tr style="background: #5D0017; color: white;"><td colspan="2" style="padding: 8px; font-weight: 700;">TOTAL LIABILITIES</td><td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalLiabilities)}</td>`;
    if (comparisonMode === 'previous') {
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(prevTotalLiabilities)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalLiabilities - prevTotalLiabilities)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${prevTotalLiabilities !== 0 ? (((totalLiabilities - prevTotalLiabilities) / Math.abs(prevTotalLiabilities)) * 100).toFixed(1) : '0.0'}%</td>`;
    }
    tableContent += '</tr>';

    // Spacer
    tableContent += `<tr><td colspan="${colCount}" style="padding: 16px;"></td></tr>`;

    // Equity
    tableContent += `<tr><td colspan="${colCount}" style="padding: 12px 8px 6px; font-size: 16px; font-weight: 700; color: #800020; border-bottom: 2px solid #800020;">EQUITY</td></tr>`;
    tableContent += buildPrintRows(displayEquity, prevEquityHierarchy);

    // Retained Earnings row
    tableContent += `<tr style="background: #f8f0f2;"><td style="padding: 6px 8px; font-weight: 600; font-style: italic;"></td><td style="padding: 6px 8px; font-weight: 600; font-style: italic;">Retained Earnings (Net Profit)</td><td style="padding: 6px 8px; text-align: right; font-weight: 600;">${formatCurrency(currentRetainedEarnings)}</td>`;
    if (comparisonMode === 'previous') {
      tableContent += `<td style="padding: 6px 8px; text-align: right;">${formatCurrency(previousRetainedEarnings)}</td>`;
      tableContent += `<td style="padding: 6px 8px; text-align: right; color: ${(currentRetainedEarnings - previousRetainedEarnings) >= 0 ? '#28A745' : '#DC3545'};">${formatCurrency(currentRetainedEarnings - previousRetainedEarnings)}</td>`;
      tableContent += `<td style="padding: 6px 8px; text-align: right;">${previousRetainedEarnings !== 0 ? (((currentRetainedEarnings - previousRetainedEarnings) / Math.abs(previousRetainedEarnings)) * 100).toFixed(1) : '0.0'}%</td>`;
    }
    tableContent += '</tr>';

    tableContent += `<tr style="background: #5D0017; color: white;"><td colspan="2" style="padding: 8px; font-weight: 700;">TOTAL EQUITY</td><td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalEquityWithRE)}</td>`;
    if (comparisonMode === 'previous') {
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(prevTotalEquityWithRE)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${formatCurrency(totalEquityWithRE - prevTotalEquityWithRE)}</td>`;
      tableContent += `<td style="padding: 8px; text-align: right; font-weight: 700;">${prevTotalEquityWithRE !== 0 ? (((totalEquityWithRE - prevTotalEquityWithRE) / Math.abs(prevTotalEquityWithRE)) * 100).toFixed(1) : '0.0'}%</td>`;
    }
    tableContent += '</tr>';

    // Spacer
    tableContent += `<tr><td colspan="${colCount}" style="padding: 8px;"></td></tr>`;

    // Total L&E
    const totalLE = totalLiabilities + totalEquityWithRE;
    const prevTotalLE = prevTotalLiabilities + prevTotalEquityWithRE;
    tableContent += `<tr style="background: #800020; color: white;"><td colspan="2" style="padding: 10px 8px; font-weight: 700; font-size: 15px;">TOTAL LIABILITIES & EQUITY</td><td style="padding: 10px 8px; text-align: right; font-weight: 700; font-size: 15px;">${formatCurrency(totalLE)}</td>`;
    if (comparisonMode === 'previous') {
      tableContent += `<td style="padding: 10px 8px; text-align: right; font-weight: 700; font-size: 15px;">${formatCurrency(prevTotalLE)}</td>`;
      tableContent += `<td style="padding: 10px 8px; text-align: right; font-weight: 700; font-size: 15px;">${formatCurrency(totalLE - prevTotalLE)}</td>`;
      tableContent += `<td style="padding: 10px 8px; text-align: right; font-weight: 700; font-size: 15px;">${prevTotalLE !== 0 ? (((totalLE - prevTotalLE) / Math.abs(prevTotalLE)) * 100).toFixed(1) : '0.0'}%</td>`;
    }
    tableContent += '</tr>';

    const headerCols = comparisonMode === 'previous'
      ? '<th style="padding: 10px 8px; text-align: left; border-bottom: 2px solid #800020;">Code</th><th style="padding: 10px 8px; text-align: left; border-bottom: 2px solid #800020;">Account</th><th style="padding: 10px 8px; text-align: right; border-bottom: 2px solid #800020;">Current Period</th><th style="padding: 10px 8px; text-align: right; border-bottom: 2px solid #800020;">Previous Period</th><th style="padding: 10px 8px; text-align: right; border-bottom: 2px solid #800020;">Change</th><th style="padding: 10px 8px; text-align: right; border-bottom: 2px solid #800020;">Change %</th>'
      : '<th style="padding: 10px 8px; text-align: left; border-bottom: 2px solid #800020;">Code</th><th style="padding: 10px 8px; text-align: left; border-bottom: 2px solid #800020;">Account</th><th style="padding: 10px 8px; text-align: right; border-bottom: 2px solid #800020;">Balance</th>';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Balance Sheet - ${activeCompany?.companyName || 'Company'}</title>
        <style>
          body { font-family: 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; color: #1A1A1A; }
          @media print {
            body { padding: 0; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 24px; border-bottom: 3px solid #800020; padding-bottom: 16px;">
          <h1 style="margin: 0 0 4px; color: #800020; font-size: 24px;">${activeCompany?.companyName || 'Company'}</h1>
          <h2 style="margin: 0 0 8px; color: #333; font-size: 18px; font-weight: 400;">Balance Sheet</h2>
          <p style="margin: 0; color: #666; font-size: 13px;">As of ${asOfDate} | Financial Year: ${fyLabel}</p>
        </div>

        <div style="display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap;">
          <div style="flex: 1; min-width: 140px; padding: 12px; background: #f8f9fa; border-left: 4px solid #800020; border-radius: 4px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Total Assets</div>
            <div style="font-size: 18px; font-weight: 700; color: #800020;">${formatCurrency(totalAssets)}</div>
          </div>
          <div style="flex: 1; min-width: 140px; padding: 12px; background: #f8f9fa; border-left: 4px solid #A0153E; border-radius: 4px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Total Liabilities</div>
            <div style="font-size: 18px; font-weight: 700; color: #A0153E;">${formatCurrency(totalLiabilities)}</div>
          </div>
          <div style="flex: 1; min-width: 140px; padding: 12px; background: #f8f9fa; border-left: 4px solid #5D0017; border-radius: 4px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Total Equity</div>
            <div style="font-size: 18px; font-weight: 700; color: #5D0017;">${formatCurrency(totalEquityWithRE)}</div>
          </div>
          <div style="flex: 1; min-width: 140px; padding: 12px; background: ${Math.abs(balanceDifference) < 0.01 ? '#f0fff0' : '#fff0f0'}; border-left: 4px solid ${Math.abs(balanceDifference) < 0.01 ? '#28A745' : '#DC3545'}; border-radius: 4px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Difference</div>
            <div style="font-size: 18px; font-weight: 700; color: ${Math.abs(balanceDifference) < 0.01 ? '#28A745' : '#DC3545'};">${formatCurrency(balanceDifference)}</div>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead><tr style="background: #f8f9fa;">${headerCols}</tr></thead>
          <tbody>${tableContent}</tbody>
        </table>

        <div style="margin-top: 24px; padding-top: 12px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 11px;">
          Generated on ${new Date().toLocaleString()} | ${activeCompany?.companyName || 'Company'}
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    addPrintBrand(printWindow);
    printWindow.document.close();
  }, [
    activeCompany, asOfDate, selectedFYId, financialYears, comparisonMode, showDetails,
    displayAssets, displayLiabilities, displayEquity,
    prevAssetHierarchy, prevLiabilityHierarchy, prevEquityHierarchy,
    totalAssets, totalLiabilities, totalEquityWithRE, balanceDifference,
    currentRetainedEarnings, previousRetainedEarnings,
    prevTotalAssets, prevTotalLiabilities, prevTotalEquityWithRE
  ]);

  // ─── Custom Tooltip ──────────────────────────────────────────────────────

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <TooltipContainer>
        <TooltipTitle>{label}</TooltipTitle>
        {payload.map((entry, index) => (
          <TooltipRow key={index}>
            <TooltipDot style={{ background: entry.color }} />
            <span>{entry.name}: {formatCurrency(entry.value)}</span>
          </TooltipRow>
        ))}
      </TooltipContainer>
    );
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    return (
      <TooltipContainer>
        <TooltipTitle>{data.name}</TooltipTitle>
        <TooltipRow>
          <span>{formatCurrency(data.value)}</span>
        </TooltipRow>
      </TooltipContainer>
    );
  };

  const renderCustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // ─── Render Hierarchy Rows ────────────────────────────────────────────────

  const renderHierarchyRows = (nodes, prevHierarchy, depth = 0) => {
    return nodes.map(node => {
      const isExpanded = expandedSections[node.id] !== false;
      const hasChildren = node.children && node.children.length > 0;
      const isGroup = hasChildren;
      const prevNode = findNodeInHierarchy(prevHierarchy || [], node.id);
      const prevBal = prevNode ? prevNode.totalBalance : 0;
      const change = node.totalBalance - prevBal;
      const changePct = prevBal !== 0 ? ((change / Math.abs(prevBal)) * 100).toFixed(1) : '0.0';

      return (
        <React.Fragment key={node.id}>
          <TableRow
            $depth={depth}
            $isGroup={isGroup}
            $isTotal={false}
            onClick={() => hasChildren ? toggleSection(node.id) : null}
            style={{ cursor: hasChildren ? 'pointer' : 'default' }}
          >
            <TableCell $depth={depth} $isGroup={isGroup}>
              <AccountCellContent>
                {hasChildren && (
                  <ExpandIcon>
                    {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                  </ExpandIcon>
                )}
                {!hasChildren && <ExpandIconSpacer />}
                <AccountCode>{node.code}</AccountCode>
                <AccountName $isGroup={isGroup}>
                  {!isGroup ? (
                    <ClickableAccountName
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/reports/ledger', {
                          state: { filterAccountId: node.id, filterAccountName: node.name }
                        });
                      }}
                    >
                      {node.name}
                    </ClickableAccountName>
                  ) : (
                    node.name
                  )}
                </AccountName>
              </AccountCellContent>
            </TableCell>
            <AmountCell $isGroup={isGroup}>
              {formatCurrency(node.totalBalance)}
            </AmountCell>
            {comparisonMode === 'previous' && (
              <>
                <AmountCell>{formatCurrency(prevBal)}</AmountCell>
                <AmountCell $positive={change >= 0}>
                  {formatCurrency(change)}
                </AmountCell>
                <AmountCell $positive={parseFloat(changePct) >= 0}>
                  {changePct}%
                </AmountCell>
              </>
            )}
          </TableRow>
          {showDetails && isExpanded && hasChildren && renderHierarchyRows(node.children, prevNode?.children || [], depth + 1)}
        </React.Fragment>
      );
    });
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const selectedFY = financialYears.find(f => f.id === selectedFYId);
  const isBalanced = Math.abs(balanceDifference) < 0.01;
  const totalLiabilitiesAndEquity = totalLiabilities + totalEquityWithRE;

  return (
    <PageLayout pageTitle="Balance Sheet" breadcrumb="Reports / Accounting Reports / Balance Sheet">
      {/* ── Action Bar ── */}
      <ActionBar>
        <ActionBarLeft>
          <ActionButton onClick={() => setShowFilters(!showFilters)}>
            <FiFilter size={14} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </ActionButton>
          <ActionButton onClick={expandAll}>
            <FiEye size={14} />
            Expand All
          </ActionButton>
          <ActionButton onClick={collapseAll}>
            <FiEyeOff size={14} />
            Collapse All
          </ActionButton>
        </ActionBarLeft>
        <ActionBarRight>
          <ActionButton onClick={exportCSV} disabled={loading || !hasGenerated}>
            <FiDownload size={14} />
            Export CSV
          </ActionButton>
          <ActionButton onClick={handlePrint} disabled={loading || !hasGenerated}>
            <FiPrinter size={14} />
            Print
          </ActionButton>
          <ActionButton $primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
            <FiRefreshCw size={14} className={loading ? 'spin' : ''} />
            {loading ? 'Loading...' : 'Generate'}
          </ActionButton>
        </ActionBarRight>
      </ActionBar>

      {/* ── Filters ── */}
      {showFilters && (
        <FiltersPanel>
          <FilterGroup>
            <FilterLabel>
              <FiCalendar size={12} />
              Financial Year
            </FilterLabel>
            <FilterSelect
              value={selectedFYId}
              onChange={e => setSelectedFYId(e.target.value)}
            >
              <option value="">Select FY</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.name || fy.label || fy.id}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <FiCalendar size={12} />
              As of Date
            </FilterLabel>
            <FilterInput
              type="date"
              value={asOfDate}
              onChange={e => setAsOfDate(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <FiLayers size={12} />
              Comparison
            </FilterLabel>
            <FilterSelect
              value={comparisonMode}
              onChange={e => {
                const mode = e.target.value;
                setComparisonMode(mode);
                // Previous-year data is only fetched on Generate — if it was not
                // loaded in the last Generate, require a re-Generate (no auto-fetch)
                if (mode === 'previous' && hasGenerated && !prevYearLoaded) {
                  setHasGenerated(false);
                }
              }}
            >
              <option value="none">None</option>
              <option value="previous">Previous Year</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <FiSearch size={12} />
              Search Accounts
            </FilterLabel>
            <SearchInputWrapper>
              <FiSearch size={14} />
              <FilterInput
                type="text"
                placeholder="Search by name or code..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '32px' }}
              />
            </SearchInputWrapper>
          </FilterGroup>

          <FilterToggleGroup>
            <ToggleWrapper>
              <ToggleSwitch $active={showDetails} onClick={() => setShowDetails(!showDetails)}>
                <ToggleKnob $active={showDetails} />
              </ToggleSwitch>
              <ToggleLabel>Show Details</ToggleLabel>
            </ToggleWrapper>
            <ToggleWrapper>
              <ToggleSwitch $active={showZeroBalances} onClick={() => setShowZeroBalances(!showZeroBalances)}>
                <ToggleKnob $active={showZeroBalances} />
              </ToggleSwitch>
              <ToggleLabel>Zero Balances</ToggleLabel>
            </ToggleWrapper>
          </FilterToggleGroup>
        </FiltersPanel>
      )}

      {/* ── Loading State ── */}
      {loading && (
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading Balance Sheet data...</LoadingText>
        </LoadingContainer>
      )}

      {/* ── Error State ── */}
      {error && !loading && (
        <ErrorContainer>
          <FiAlertCircle size={24} />
          <ErrorText>{error}</ErrorText>
          <ActionButton onClick={handleGenerateReport}>
            <FiRefreshCw size={14} />
            Retry
          </ActionButton>
        </ErrorContainer>
      )}

      {/* ── Not Generated Yet State ── */}
      {!loading && !error && !hasGenerated && (
        <EmptyContainer>
          <FiCalendar size={48} />
          <EmptyTitle>Generate Balance Sheet</EmptyTitle>
          <EmptyText>Select a financial year and as-of date, then click Generate to view the report.</EmptyText>
        </EmptyContainer>
      )}

      {/* ── Empty State ── */}
      {!loading && !error && hasGenerated && accounts.length === 0 && (
        <EmptyContainer>
          <FiDollarSign size={48} />
          <EmptyTitle>No Accounts Found</EmptyTitle>
          <EmptyText>Set up your Chart of Accounts to generate a Balance Sheet report.</EmptyText>
        </EmptyContainer>
      )}

      {/* ── Report Content ── */}
      {!loading && !error && hasGenerated && accounts.length > 0 && (
        <>
          {/* ── Summary Cards ── */}
          <SummaryCardsGrid>
            <SummaryCard $borderColor={colors.chart.primary}>
              <SummaryCardIcon $bgColor={colors.chart.primary}>
                <FiDollarSign size={20} />
              </SummaryCardIcon>
              <SummaryCardContent>
                <SummaryCardLabel>Total Assets</SummaryCardLabel>
                <SummaryCardValue>{formatCurrency(totalAssets)}</SummaryCardValue>
                {comparisonMode === 'previous' && (
                  <SummaryCardChange $positive={totalAssets >= prevTotalAssets}>
                    {totalAssets >= prevTotalAssets ? '+' : ''}{prevTotalAssets !== 0 ? (((totalAssets - prevTotalAssets) / Math.abs(prevTotalAssets)) * 100).toFixed(1) : '0.0'}% vs prev
                  </SummaryCardChange>
                )}
              </SummaryCardContent>
            </SummaryCard>

            <SummaryCard $borderColor={colors.chart.secondary}>
              <SummaryCardIcon $bgColor={colors.chart.secondary}>
                <FiTrendingUp size={20} />
              </SummaryCardIcon>
              <SummaryCardContent>
                <SummaryCardLabel>Total Liabilities</SummaryCardLabel>
                <SummaryCardValue>{formatCurrency(totalLiabilities)}</SummaryCardValue>
                {comparisonMode === 'previous' && (
                  <SummaryCardChange $positive={totalLiabilities <= prevTotalLiabilities}>
                    {totalLiabilities >= prevTotalLiabilities ? '+' : ''}{prevTotalLiabilities !== 0 ? (((totalLiabilities - prevTotalLiabilities) / Math.abs(prevTotalLiabilities)) * 100).toFixed(1) : '0.0'}% vs prev
                  </SummaryCardChange>
                )}
              </SummaryCardContent>
            </SummaryCard>

            <SummaryCard $borderColor={colors.chart.tertiary}>
              <SummaryCardIcon $bgColor={colors.chart.tertiary}>
                <FiLayers size={20} />
              </SummaryCardIcon>
              <SummaryCardContent>
                <SummaryCardLabel>Total Equity</SummaryCardLabel>
                <SummaryCardValue>{formatCurrency(totalEquityWithRE)}</SummaryCardValue>
                {comparisonMode === 'previous' && (
                  <SummaryCardChange $positive={totalEquityWithRE >= prevTotalEquityWithRE}>
                    {totalEquityWithRE >= prevTotalEquityWithRE ? '+' : ''}{prevTotalEquityWithRE !== 0 ? (((totalEquityWithRE - prevTotalEquityWithRE) / Math.abs(prevTotalEquityWithRE)) * 100).toFixed(1) : '0.0'}% vs prev
                  </SummaryCardChange>
                )}
              </SummaryCardContent>
            </SummaryCard>

            <SummaryCard $borderColor={isBalanced ? colors.success.main : colors.error.main}>
              <SummaryCardIcon $bgColor={isBalanced ? colors.success.main : colors.error.main}>
                {isBalanced ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
              </SummaryCardIcon>
              <SummaryCardContent>
                <SummaryCardLabel>Difference (A - L - E)</SummaryCardLabel>
                <SummaryCardValue $color={isBalanced ? colors.success.main : colors.error.main}>
                  {formatCurrency(balanceDifference)}
                </SummaryCardValue>
                <SummaryCardChange $positive={isBalanced}>
                  {isBalanced ? 'Balanced' : 'Out of Balance'}
                </SummaryCardChange>
              </SummaryCardContent>
            </SummaryCard>
          </SummaryCardsGrid>

          {/* ── Balance Sheet Table ── */}
          <TableCard ref={printRef}>
            <TableHeader>
              <TableHeaderLeft>
                <TableHeaderTitle>Balance Sheet Statement</TableHeaderTitle>
                <TableHeaderSubtitle>
                  As of {asOfDate}
                  {selectedFY && ` | FY: ${selectedFY.name || selectedFY.label || selectedFY.id}`}
                  {` | ${cutoffTransactions.length} transactions`}
                </TableHeaderSubtitle>
              </TableHeaderLeft>
            </TableHeader>

            <TableWrapper>
              <StyledTable>
                <thead>
                  <tr>
                    <TableHeaderCell>Account</TableHeaderCell>
                    <TableHeaderCell $align="right">
                      {comparisonMode === 'previous' ? 'Current Period' : 'Balance'}
                    </TableHeaderCell>
                    {comparisonMode === 'previous' && (
                      <>
                        <TableHeaderCell $align="right">Previous Period</TableHeaderCell>
                        <TableHeaderCell $align="right">Change</TableHeaderCell>
                        <TableHeaderCell $align="right">Change %</TableHeaderCell>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {/* ── ASSETS SECTION ── */}
                  <SectionHeaderRow onClick={() => toggleSection('section-asset')}>
                    <SectionHeaderCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                      <SectionHeaderContent>
                        {expandedSections['section-asset'] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        <span>ASSETS</span>
                      </SectionHeaderContent>
                    </SectionHeaderCell>
                  </SectionHeaderRow>

                  {expandedSections['section-asset'] && (
                    <>
                      {displayAssets.length > 0 ? (
                        renderHierarchyRows(displayAssets, prevAssetHierarchy)
                      ) : (
                        <TableRow>
                          <TableCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                            <NoDataText>No asset accounts found</NoDataText>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}

                  <TotalRow $variant="primary">
                    <TotalCell>TOTAL ASSETS</TotalCell>
                    <TotalAmountCell>{formatCurrency(totalAssets)}</TotalAmountCell>
                    {comparisonMode === 'previous' && (
                      <>
                        <TotalAmountCell>{formatCurrency(prevTotalAssets)}</TotalAmountCell>
                        <TotalAmountCell $positive={(totalAssets - prevTotalAssets) >= 0}>
                          {formatCurrency(totalAssets - prevTotalAssets)}
                        </TotalAmountCell>
                        <TotalAmountCell>
                          {prevTotalAssets !== 0 ? (((totalAssets - prevTotalAssets) / Math.abs(prevTotalAssets)) * 100).toFixed(1) : '0.0'}%
                        </TotalAmountCell>
                      </>
                    )}
                  </TotalRow>

                  {/* ── Spacer ── */}
                  <SpacerRow>
                    <td colSpan={comparisonMode === 'previous' ? 5 : 2}>&nbsp;</td>
                  </SpacerRow>

                  {/* ── LIABILITIES SECTION ── */}
                  <SectionHeaderRow onClick={() => toggleSection('section-liability')}>
                    <SectionHeaderCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                      <SectionHeaderContent>
                        {expandedSections['section-liability'] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        <span>LIABILITIES</span>
                      </SectionHeaderContent>
                    </SectionHeaderCell>
                  </SectionHeaderRow>

                  {expandedSections['section-liability'] && (
                    <>
                      {displayLiabilities.length > 0 ? (
                        renderHierarchyRows(displayLiabilities, prevLiabilityHierarchy)
                      ) : (
                        <TableRow>
                          <TableCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                            <NoDataText>No liability accounts found</NoDataText>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}

                  <TotalRow $variant="secondary">
                    <TotalCell>TOTAL LIABILITIES</TotalCell>
                    <TotalAmountCell>{formatCurrency(totalLiabilities)}</TotalAmountCell>
                    {comparisonMode === 'previous' && (
                      <>
                        <TotalAmountCell>{formatCurrency(prevTotalLiabilities)}</TotalAmountCell>
                        <TotalAmountCell $positive={(totalLiabilities - prevTotalLiabilities) <= 0}>
                          {formatCurrency(totalLiabilities - prevTotalLiabilities)}
                        </TotalAmountCell>
                        <TotalAmountCell>
                          {prevTotalLiabilities !== 0 ? (((totalLiabilities - prevTotalLiabilities) / Math.abs(prevTotalLiabilities)) * 100).toFixed(1) : '0.0'}%
                        </TotalAmountCell>
                      </>
                    )}
                  </TotalRow>

                  {/* ── Spacer ── */}
                  <SpacerRow>
                    <td colSpan={comparisonMode === 'previous' ? 5 : 2}>&nbsp;</td>
                  </SpacerRow>

                  {/* ── EQUITY SECTION ── */}
                  <SectionHeaderRow onClick={() => toggleSection('section-equity')}>
                    <SectionHeaderCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                      <SectionHeaderContent>
                        {expandedSections['section-equity'] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        <span>EQUITY</span>
                      </SectionHeaderContent>
                    </SectionHeaderCell>
                  </SectionHeaderRow>

                  {expandedSections['section-equity'] && (
                    <>
                      {displayEquity.length > 0 ? (
                        renderHierarchyRows(displayEquity, prevEquityHierarchy)
                      ) : (
                        <TableRow>
                          <TableCell colSpan={comparisonMode === 'previous' ? 5 : 2}>
                            <NoDataText>No equity accounts found</NoDataText>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Retained Earnings */}
                      <RetainedEarningsRow>
                        <TableCell>
                          <AccountCellContent>
                            <ExpandIconSpacer />
                            <RetainedEarningsLabel>Retained Earnings (Net Profit)</RetainedEarningsLabel>
                          </AccountCellContent>
                        </TableCell>
                        <AmountCell $isGroup style={{ fontWeight: 600, fontStyle: 'italic' }}>
                          {formatCurrency(currentRetainedEarnings)}
                        </AmountCell>
                        {comparisonMode === 'previous' && (
                          <>
                            <AmountCell>{formatCurrency(previousRetainedEarnings)}</AmountCell>
                            <AmountCell $positive={(currentRetainedEarnings - previousRetainedEarnings) >= 0}>
                              {formatCurrency(currentRetainedEarnings - previousRetainedEarnings)}
                            </AmountCell>
                            <AmountCell>
                              {previousRetainedEarnings !== 0 ? (((currentRetainedEarnings - previousRetainedEarnings) / Math.abs(previousRetainedEarnings)) * 100).toFixed(1) : '0.0'}%
                            </AmountCell>
                          </>
                        )}
                      </RetainedEarningsRow>
                    </>
                  )}

                  <TotalRow $variant="secondary">
                    <TotalCell>TOTAL EQUITY</TotalCell>
                    <TotalAmountCell>{formatCurrency(totalEquityWithRE)}</TotalAmountCell>
                    {comparisonMode === 'previous' && (
                      <>
                        <TotalAmountCell>{formatCurrency(prevTotalEquityWithRE)}</TotalAmountCell>
                        <TotalAmountCell $positive={(totalEquityWithRE - prevTotalEquityWithRE) >= 0}>
                          {formatCurrency(totalEquityWithRE - prevTotalEquityWithRE)}
                        </TotalAmountCell>
                        <TotalAmountCell>
                          {prevTotalEquityWithRE !== 0 ? (((totalEquityWithRE - prevTotalEquityWithRE) / Math.abs(prevTotalEquityWithRE)) * 100).toFixed(1) : '0.0'}%
                        </TotalAmountCell>
                      </>
                    )}
                  </TotalRow>

                  {/* ── Spacer ── */}
                  <SpacerRow>
                    <td colSpan={comparisonMode === 'previous' ? 5 : 2}>&nbsp;</td>
                  </SpacerRow>

                  {/* ── GRAND TOTAL ── */}
                  <GrandTotalRow>
                    <TotalCell>TOTAL LIABILITIES & EQUITY</TotalCell>
                    <TotalAmountCell>{formatCurrency(totalLiabilitiesAndEquity)}</TotalAmountCell>
                    {comparisonMode === 'previous' && (
                      <>
                        <TotalAmountCell>{formatCurrency(prevTotalLiabilities + prevTotalEquityWithRE)}</TotalAmountCell>
                        <TotalAmountCell>
                          {formatCurrency(totalLiabilitiesAndEquity - (prevTotalLiabilities + prevTotalEquityWithRE))}
                        </TotalAmountCell>
                        <TotalAmountCell>
                          {(prevTotalLiabilities + prevTotalEquityWithRE) !== 0
                            ? (((totalLiabilitiesAndEquity - (prevTotalLiabilities + prevTotalEquityWithRE)) / Math.abs(prevTotalLiabilities + prevTotalEquityWithRE)) * 100).toFixed(1)
                            : '0.0'}%
                        </TotalAmountCell>
                      </>
                    )}
                  </GrandTotalRow>

                  {/* ── Balance Verification ── */}
                  <BalanceVerificationRow $balanced={isBalanced}>
                    <td colSpan={comparisonMode === 'previous' ? 5 : 2}>
                      <BalanceVerificationContent>
                        {isBalanced ? <FiCheckCircle size={16} /> : <FiAlertCircle size={16} />}
                        <span>
                          {isBalanced
                            ? 'Balance Sheet is balanced. Assets = Liabilities + Equity.'
                            : `Balance Sheet is out of balance by ${formatCurrency(balanceDifference)}. Please review your transactions.`
                          }
                        </span>
                      </BalanceVerificationContent>
                    </td>
                  </BalanceVerificationRow>
                </tbody>
              </StyledTable>
            </TableWrapper>
          </TableCard>

          {/* ── Charts ── */}
          <ChartsGrid>
            {/* Bar Chart: Assets vs Liabilities vs Equity */}
            <ChartCard>
              <ChartTitle>Assets vs Liabilities vs Equity</ChartTitle>
              <ChartBody>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barChartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.border.light} />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v, { compact: true })} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={80} fontSize={12} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="current" name="Current Period" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={comparisonMode === 'previous' ? 18 : 28} />
                    {comparisonMode === 'previous' && (
                      <Bar dataKey="previous" name="Previous Period" fill={colors.chart.accent1} radius={[0, 4, 4, 0]} barSize={18} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </ChartBody>
            </ChartCard>

            {/* Pie: Asset Composition */}
            <ChartCard>
              <ChartTitle>Asset Composition</ChartTitle>
              <ChartBody>
                {assetPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={assetPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={40}
                        paddingAngle={2}
                        dataKey="value"
                        label={renderCustomPieLabel}
                        labelLine={false}
                      >
                        {assetPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: '8px' }}
                        formatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <NoChartData>No asset data to display</NoChartData>
                )}
              </ChartBody>
            </ChartCard>

            {/* Pie: Liability + Equity Composition */}
            <ChartCard>
              <ChartTitle>Liabilities & Equity Composition</ChartTitle>
              <ChartBody>
                {liabEquityPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={liabEquityPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={40}
                        paddingAngle={2}
                        dataKey="value"
                        label={renderCustomPieLabel}
                        labelLine={false}
                      >
                        {liabEquityPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: '8px' }}
                        formatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <NoChartData>No liability/equity data to display</NoChartData>
                )}
              </ChartBody>
            </ChartCard>
          </ChartsGrid>
        </>
      )}
    </PageLayout>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

// Action Bar
const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.sm};
`;

const ActionBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid ${props => props.$primary ? colors.primary.main : colors.border.main};
  background: ${props => props.$primary ? colors.primary.main : colors.background.default};
  color: ${props => props.$primary ? colors.text.white : colors.text.primary};

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? colors.primary.dark : colors.neutral[100]};
    border-color: ${props => props.$primary ? colors.primary.dark : colors.border.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Filters
const FiltersPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
  flex: 1;
  max-width: 240px;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.secondary};
`;

const FilterSelect = styled.select`
  padding: 8px 10px;
  font-size: ${typography.fontSize.sm};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  color: ${colors.text.primary};
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px rgba(128, 0, 32, 0.1);
  }
`;

const FilterInput = styled.input`
  padding: 8px 10px;
  font-size: ${typography.fontSize.sm};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  color: ${colors.text.primary};
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px rgba(128, 0, 32, 0.1);
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;

  > svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.text.tertiary};
  }
`;

const FilterToggleGroup = styled.div`
  display: flex;
  gap: ${spacing.md};
  align-items: center;
  padding-bottom: 2px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSwitch = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${props => props.$active ? colors.primary.main : colors.neutral[400]};
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease;
`;

const ToggleKnob = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  left: ${props => props.$active ? '18px' : '2px'};
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ToggleLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

// Loading / Error / Empty States
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: ${spacing.md};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.border.light};
  border-top-color: ${colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: ${spacing.md};
  color: ${colors.error.main};
`;

const ErrorText = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.error.main};
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: ${spacing.md};
  color: ${colors.text.tertiary};
`;

const EmptyTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.tertiary};
  margin: 0;
`;

// Summary Cards
const SummaryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing.md};
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md} ${spacing.lg};
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  border-left: 4px solid ${props => props.$borderColor || colors.primary.main};
  box-shadow: ${shadows.sm};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
  }
`;

const SummaryCardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$bgColor || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.white};
  flex-shrink: 0;
`;

const SummaryCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryCardLabel = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  margin-bottom: 2px;
`;

const SummaryCardValue = styled.div`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.$color || colors.text.primary};
  line-height: 1.2;
`;

const SummaryCardChange = styled.div`
  font-size: 11px;
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.$positive ? colors.success.main : colors.error.main};
  margin-top: 2px;
`;

// Charts
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${spacing.md};
`;

const ChartCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

const ChartTitle = styled.h3`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  padding: ${spacing.md};
  margin: 0;
  border-bottom: 1px solid ${colors.border.light};
`;

const ChartBody = styled.div`
  padding: ${spacing.md};
`;

const NoChartData = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSize.sm};
`;

// Tooltips
const TooltipContainer = styled.div`
  background: ${colors.secondary.main};
  border-radius: ${borderRadius.sm};
  padding: 10px 14px;
  box-shadow: ${shadows.md};
  border: 1px solid ${colors.secondary.light};
`;

const TooltipTitle = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.white};
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  padding: 2px 0;
`;

const TooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// Table
const TableCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.background.paper};
`;

const TableHeaderLeft = styled.div``;

const TableHeaderTitle = styled.h3`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  margin: 0 0 2px;
`;

const TableHeaderSubtitle = styled.p`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  margin: 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSize.sm};
`;

const TableHeaderCell = styled.th`
  padding: 10px ${spacing.md};
  text-align: ${props => props.$align || 'left'};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.primary.main};
  background: ${colors.background.paper};
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const SectionHeaderRow = styled.tr`
  cursor: pointer;
  &:hover {
    background: rgba(128, 0, 32, 0.04);
  }
`;

const SectionHeaderCell = styled.td`
  padding: 12px ${spacing.md};
  border-bottom: 2px solid ${colors.primary.main};
  background: rgba(128, 0, 32, 0.06);
`;

const SectionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.tr`
  background: ${props => {
    if (props.$isTotal) return colors.neutral[50];
    if (props.$isGroup && props.$depth === 0) return 'rgba(128, 0, 32, 0.02)';
    return 'transparent';
  }};
  border-bottom: 1px solid ${colors.border.light};
  transition: background 0.15s ease;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TableCell = styled.td`
  padding: 8px ${spacing.md};
  padding-left: ${props => `${16 + (props.$depth || 0) * 24}px`};
  vertical-align: middle;
`;

const AccountCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: ${colors.text.secondary};
  flex-shrink: 0;
`;

const ExpandIconSpacer = styled.span`
  width: 18px;
  flex-shrink: 0;
`;

const AccountCode = styled.span`
  font-size: 11px;
  color: ${colors.text.tertiary};
  font-family: ${typography.fontFamily.mono};
  min-width: 50px;
`;

const AccountName = styled.span`
  font-weight: ${props => props.$isGroup ? typography.fontWeight.semibold : typography.fontWeight.regular};
  color: ${props => props.$isGroup ? colors.text.primary : colors.text.secondary};
`;

const ClickableAccountName = styled.span`
  cursor: pointer;
  color: #1a73e8;
  &:hover {
    text-decoration: underline;
  }
`;

const AmountCell = styled.td`
  padding: 8px ${spacing.md};
  text-align: right;
  font-weight: ${props => props.$isGroup ? typography.fontWeight.semibold : typography.fontWeight.regular};
  color: ${props => {
    if (props.$positive === true) return colors.success.main;
    if (props.$positive === false) return colors.error.main;
    return colors.text.primary;
  }};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const NoDataText = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
  font-style: italic;
`;

const TotalRow = styled.tr`
  background: ${props => {
    if (props.$variant === 'primary') return colors.primary.main;
    if (props.$variant === 'secondary') return colors.primary.dark;
    return colors.neutral[800];
  }};
`;

const TotalCell = styled.td`
  padding: 10px ${spacing.md};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.white};
  font-size: ${typography.fontSize.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TotalAmountCell = styled.td`
  padding: 10px ${spacing.md};
  text-align: right;
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.white};
  font-size: ${typography.fontSize.sm};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const SpacerRow = styled.tr`
  > td {
    padding: 6px;
    border: none;
    background: transparent;
  }
`;

const RetainedEarningsRow = styled.tr`
  background: rgba(128, 0, 32, 0.04);
  border-bottom: 1px solid ${colors.border.light};
  border-top: 1px dashed ${colors.border.main};
`;

const RetainedEarningsLabel = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  font-style: italic;
  color: ${colors.primary.main};
`;

const GrandTotalRow = styled.tr`
  background: ${colors.primary.main};
  border-top: 3px solid ${colors.primary.dark};
`;

const BalanceVerificationRow = styled.tr`
  background: ${props => props.$balanced ? 'rgba(40, 167, 69, 0.08)' : 'rgba(220, 53, 69, 0.08)'};
`;

const BalanceVerificationContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px ${spacing.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: inherit;

  > svg {
    flex-shrink: 0;
  }
`;

export default BalanceSheet;
